// HTML içeriğini yazdır diyaloğu açmadan doğrudan PDF dosyası olarak indirir.

const MM_PER_INCH = 25.4;
const CSS_DPI = 96;
const MAX_CANVAS_SIDE = 32000;
const MAX_CANVAS_AREA = 240000000;
const TARGET_SCALE = 2;
const JPEG_QUALITY = 0.92;
const MIN_SCALE = 0.5;
const IMAGE_TIMEOUT = 15000;
const RENDER_HOST_CLASS = "pdf-render-host";
const FONT_NAME = "Roboto";
const DEFAULT_MARGIN = { top: 10, right: 8, bottom: 10, left: 8 };
const HEADER_GAP = 3;
const FOOTER_GAP = 3;
const MIN_CONTENT_HEIGHT = 20;
const MIN_PAGE_CONTENT_PX = 40;
const LINE_HEIGHT_RATIO = 0.4;
const HEADER_FONT_SIZE = 14;
const FOOTER_FONT_SIZE = 9;
const PAGE_LABEL_FONT_SIZE = 8;
const WATERMARK_FONT_SIZE = 13;
const WATERMARK_OPACITY = 0.16;
const WATERMARK_COLUMNS = 3;
const WATERMARK_ROWS = 4;
const TEXT_DARK_RGB = [15, 23, 42];
const TEXT_MUTED_RGB = [100, 116, 139];

const pxToMm = (px) => (px * MM_PER_INCH) / CSS_DPI;
const mmToPx = (mm) => (mm * CSS_DPI) / MM_PER_INCH;

// Render ana thread'i bloke ettiğinden, önce tarayıcıya loading göstergesini boyama fırsatı verilir.
export function waitForPaint() {
  return new Promise((resolve) => {
    if (typeof window.requestAnimationFrame !== "function") {
      resolve();
      return;
    }
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
  });
}

// Metin çizilecekse Türkçe karakter desteği için Roboto fontları da yüklenir.
function loadModules(needsText) {
  const modules = [import("jspdf"), import("html2canvas")];
  if (needsText) {
    modules.push(import("@/assets/fonts/Roboto-Regular-base64.js"));
    modules.push(import("@/assets/fonts/Roboto-Bold-base64.js"));
  }
  return Promise.all(modules);
}

// Gövde parçası da tam doküman da kabul edilir; stiller ayrıştırılıp host altına alınır.
function extractDocument(html) {
  const parsed = new DOMParser().parseFromString(html || "", "text/html");
  const styleNodes = parsed.querySelectorAll("style");
  const styles = Array.prototype.map.call(styleNodes, (node) => node.textContent).join("\n");
  Array.prototype.forEach.call(styleNodes, (node) => node.parentNode.removeChild(node));
  return { body: parsed.body.innerHTML, styles: styles, bodyClass: parsed.body.className };
}

function findBlockEnd(css, openIndex) {
  let depth = 0;
  let index = openIndex;
  while (index < css.length) {
    if (css[index] === "{") {
      depth++;
    } else if (css[index] === "}") {
      depth--;
      if (depth === 0) {
        return index;
      }
    }
    index++;
  }
  return css.length;
}

function scopeSingleSelector(selector, scopeSelector) {
  if (!selector) {
    return "";
  }
  if (/^(html|body|:root)$/i.test(selector)) {
    return scopeSelector;
  }
  const rootWithQualifier = selector.match(/^(?:html|body|:root)([.#:[][^\s]*)?([\s>+~][\s\S]*)?$/i);
  if (rootWithQualifier) {
    return `${scopeSelector}${rootWithQualifier[1] || ""}${rootWithQualifier[2] || ""}`;
  }
  return `${scopeSelector} ${selector}`;
}

function scopeSelectorList(prelude, scopeSelector) {
  return prelude
    .split(",")
    .map((selector) => scopeSingleSelector(selector.trim(), scopeSelector))
    .filter((selector) => selector)
    .join(", ");
}

// Ekran CSS'i sayfaya sızmasın diye her kural host seçicisinin altına alınır.
function scopeCssBlocks(css, scopeSelector) {
  let result = "";
  let index = 0;
  while (index < css.length) {
    const braceIndex = css.indexOf("{", index);
    if (braceIndex < 0) {
      break;
    }
    const prelude = css.slice(index, braceIndex).trim();
    const blockEnd = findBlockEnd(css, braceIndex);
    const body = css.slice(braceIndex + 1, blockEnd);
    index = blockEnd + 1;
    if (!prelude || /^@page/i.test(prelude)) {
      continue;
    }
    if (/^@media/i.test(prelude)) {
      // Çıktı zaten baskı görünümü olduğundan print kuralları koşulsuz uygulanır ("not print" hariç).
      result += /print/i.test(prelude) && !/\bnot\s+print\b/i.test(prelude)
        ? scopeCssBlocks(body, scopeSelector)
        : `${prelude}{${scopeCssBlocks(body, scopeSelector)}}`;
    } else if (/^@(supports|-moz-document|layer|container)/i.test(prelude)) {
      result += `${prelude}{${scopeCssBlocks(body, scopeSelector)}}`;
    } else if (prelude.charAt(0) === "@") {
      result += `${prelude}{${body}}`;
    } else {
      result += `${scopeSelectorList(prelude, scopeSelector)}{${body}}`;
    }
  }
  return result;
}

function scopeCss(css, scopeSelector) {
  return scopeCssBlocks((css || "").replace(/\/\*[\s\S]*?\*\//g, ""), scopeSelector);
}

// İçerik ana dokümanda ekran dışına kurulur; iframe içinde html2canvas klonlama adımı takılıyor.
function createRenderHost(html, widthPx, hostClass) {
  const parts = extractDocument(html);
  const classNames = [RENDER_HOST_CLASS, hostClass, parts.bodyClass].filter((name) => name).join(" ");
  const host = document.createElement("div");
  host.className = classNames;
  host.setAttribute("aria-hidden", "true");
  // Sayfa kökü lang="en" olduğundan, text-transform Türkçe başlıkları bozmasın diye dil host'ta belirtilir.
  // "trjandarma" gibi değerler geçerli bir dil etiketi olmadığından iki harfe indirgenir.
  host.lang = (localStorage.preferredLanguage || "tr").toLowerCase().slice(0, 2);
  host.style.width = `${widthPx}px`;
  // İçerik doğrudan host'a konur; araya sarmalayıcı girerse "body > x" gibi seçiciler kayar.
  host.innerHTML = parts.body;
  if (parts.styles) {
    const style = document.createElement("style");
    style.textContent = scopeCss(parts.styles, `.${RENDER_HOST_CLASS}`);
    host.insertBefore(style, host.firstChild);
  }
  document.body.appendChild(host);
  return host;
}

function removeRenderHost(host) {
  if (host.parentNode) {
    host.parentNode.removeChild(host);
  }
}

// Sayfa bölünmesine izin verilen noktalar: verilen blokların alt kenarları.
function collectBreakPoints(host, selectors) {
  const hostTop = host.getBoundingClientRect().top;
  const points = [];
  selectors.forEach((selector) => {
    const nodes = host.querySelectorAll(selector);
    Array.prototype.forEach.call(nodes, (node) => {
      const rect = node.getBoundingClientRect();
      if (rect.height > 0) {
        points.push(rect.bottom - hostTop);
      }
    });
  });
  points.sort((first, second) => first - second);
  return points.filter((point, index) => index === 0 || point - points[index - 1] > 0.5);
}

// Sayfa devrinde tekrarlanacak tablo başlıklarının konumları toplanır.
function collectTableHeaders(host, tableSelector) {
  const hostTop = host.getBoundingClientRect().top;
  const headers = [];
  Array.prototype.forEach.call(host.querySelectorAll(tableSelector), (table) => {
    const head = table.querySelector("thead");
    if (!head) {
      return;
    }
    const headRect = head.getBoundingClientRect();
    const tableRect = table.getBoundingClientRect();
    if (headRect.height <= 0) {
      return;
    }
    headers.push({
      top: headRect.top - hostTop,
      height: headRect.height,
      tableTop: tableRect.top - hostTop,
      tableBottom: tableRect.bottom - hostTop,
    });
  });
  return headers;
}

// Sayfa, başlığı geride kalmış bir tablonun ortasından başlıyorsa o başlık tekrarlanır.
function findRepeatHeader(headers, start) {
  let repeat = null;
  headers.forEach((header) => {
    const isInsideTable = header.tableTop < start && start < header.tableBottom;
    const isHeaderPassed = header.top + header.height <= start;
    if (isInsideTable && isHeaderPassed && (!repeat || header.tableTop > repeat.tableTop)) {
      repeat = header;
    }
  });
  return repeat;
}

// Her sayfa, sığan son blok sınırında kesilir; böylece içerik ortadan bölünmez.
function computePages(totalHeight, pageHeightPx, points, headers) {
  const pages = [];
  let start = 0;
  let pointIndex = 0;
  while (start < totalHeight) {
    const repeat = findRepeatHeader(headers, start);
    const available = Math.max(MIN_PAGE_CONTENT_PX, pageHeightPx - (repeat ? repeat.height : 0));
    if (totalHeight - start <= available + 1) {
      pages.push({ start: start, end: totalHeight, repeat: repeat });
      break;
    }
    const limit = start + available;
    let next = 0;
    while (pointIndex < points.length && points[pointIndex] <= limit) {
      if (points[pointIndex] > start) {
        next = points[pointIndex];
      }
      pointIndex++;
    }
    const end = next > start ? next : limit;
    pages.push({ start: start, end: end, repeat: repeat });
    start = end;
  }
  return pages;
}

// Canvas kenar ve alan limitlerini aşmayacak en yüksek çözünürlük seçilir.
function resolveScale(widthPx, heightPx) {
  const bySide = MAX_CANVAS_SIDE / Math.max(widthPx, heightPx, 1);
  const byArea = Math.sqrt(MAX_CANVAS_AREA / Math.max(widthPx * heightPx, 1));
  return Math.max(MIN_SCALE, Math.min(TARGET_SCALE, bySide, byArea));
}

// Tek canvas'a sığmayan uzun içerik hedef çözünürlükte kalabilmek için bantlara bölünür.
function resolveBandHeight(widthPx) {
  const bySide = MAX_CANVAS_SIDE / TARGET_SCALE;
  const byArea = MAX_CANVAS_AREA / Math.max(widthPx * TARGET_SCALE, 1) / TARGET_SCALE;
  return Math.max(1, Math.min(bySide, byArea));
}

// Bant sınırları sayfa sınırlarıyla hizalanır; böylece bir sayfa tek bir canvas'tan dilimlenir.
function buildBands(pages, maxBandHeight) {
  const bands = [];
  pages.forEach((page) => {
    const current = bands[bands.length - 1];
    if (!current || page.end - current.top > maxBandHeight) {
      bands.push({ top: page.start, height: page.end - page.start });
    } else {
      current.height = page.end - current.top;
    }
    page.bandIndex = bands.length - 1;
  });
  return bands;
}

function renderRegion(host, html2canvas, region, options) {
  return html2canvas(host, {
    scale: resolveScale(region.width, region.height),
    backgroundColor: options.backgroundColor,
    useCORS: true,
    logging: false,
    imageTimeout: IMAGE_TIMEOUT,
    y: region.top,
    height: region.height,
    // html2canvas tüm dokümanı klonlar; sayfanın geri kalanı atlanmazsa büyük ekranlarda çok yavaşlıyor.
    ignoreElements: (element) =>
      !host.contains(element) && !element.contains(host) && !document.head.contains(element),
  });
}

// Her bant ve tekrarlanacak her tablo başlığı sırayla render edilir.
function renderRegions(host, html2canvas, regions, options) {
  return regions.reduce(
    (chain, region) =>
      chain.then((rendered) =>
        renderRegion(host, html2canvas, region, options).then((canvas) => {
          region.canvas = canvas;
          rendered.push(region);
          return rendered;
        }),
      ),
    Promise.resolve([]),
  );
}

// Sayfa/bant planı çıkarılır; yalnızca küçük olan başlık şeritleri baştan render edilir.
function capturePlan(host, html2canvas, options) {
  const totalHeight = Math.max(host.scrollHeight, host.offsetHeight);
  const totalWidth = Math.max(host.scrollWidth, host.offsetWidth);
  const pages = computePages(
    totalHeight,
    options.pageHeightPx,
    collectBreakPoints(host, options.breakSelectors),
    collectTableHeaders(host, options.repeatHeaderSelector),
  );
  const bands = buildBands(pages, resolveBandHeight(totalWidth));
  bands.forEach((band) => {
    band.width = totalWidth;
  });
  const repeats = [];
  pages.forEach((page) => {
    if (page.repeat && repeats.indexOf(page.repeat) < 0) {
      page.repeat.width = totalWidth;
      repeats.push(page.repeat);
    }
  });

  return renderRegions(host, html2canvas, repeats, options).then(() => ({
    pages: pages,
    bands: bands,
  }));
}

function createSlice(canvas, top, height, backgroundColor) {
  const slice = document.createElement("canvas");
  slice.width = canvas.width;
  slice.height = height;
  const context = slice.getContext("2d");
  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, slice.width, slice.height);
  context.drawImage(canvas, 0, top, canvas.width, height, 0, 0, canvas.width, height);
  return slice;
}

function buildLines(doc, text, fontSize, width, bold) {
  if (!text) {
    return [];
  }
  doc.setFont(FONT_NAME, bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  return doc.splitTextToSize(text, width);
}

function drawLines(doc, lines, fontSize, bold, colorRgb, centerX, baselineY) {
  doc.setFont(FONT_NAME, bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor.apply(doc, colorRgb);
  lines.forEach((line, index) => {
    doc.text(line, centerX, baselineY + index * fontSize * LINE_HEIGHT_RATIO, { align: "center" });
  });
}

// Filigranı PDF sayfasına vektör metin olarak çizer; jsPDF ile üretilen diğer raporlar da kullanır.
export function drawPdfWatermark(doc, text, pageWidth, pageHeight) {
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: WATERMARK_OPACITY }));
  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(WATERMARK_FONT_SIZE);
  doc.setTextColor(0, 0, 0);
  const stepX = pageWidth / WATERMARK_COLUMNS;
  const stepY = pageHeight / WATERMARK_ROWS;
  let row = 0;
  while (row < WATERMARK_ROWS) {
    let column = 0;
    while (column < WATERMARK_COLUMNS) {
      doc.text(text, stepX * (column + 0.5), stepY * (row + 0.5), { align: "center", angle: 45 });
      column++;
    }
    row++;
  }
  doc.restoreGraphicsState();
}

// Bir bölgenin canvas'ından istenen dikey aralığı kırpıp basar, kapladığı yüksekliği döndürür.
function drawCanvasBand(doc, region, offsetPx, heightPx, layout, topMm) {
  const canvas = region.canvas;
  if (!canvas || heightPx <= 0) {
    return 0;
  }
  const pixelRatio = canvas.height / Math.max(region.height, 1);
  const top = Math.round(offsetPx * pixelRatio);
  const height = Math.min(Math.round(heightPx * pixelRatio), canvas.height - top);
  if (height <= 0) {
    return 0;
  }
  // Genişlik contentWidth'e sabitlendiğinden yükseklik de aynı oranla küçültülür, en-boy korunur.
  const heightMm = pxToMm(height / pixelRatio) / layout.renderScale;
  // jsPDF PNG'yi sıkıştırmasız gömdüğünden sayfa görüntüsü JPEG olarak verilir.
  doc.addImage(
    createSlice(canvas, top, height, layout.backgroundColor).toDataURL("image/jpeg", JPEG_QUALITY),
    "JPEG",
    layout.margin.left,
    topMm,
    layout.contentWidth,
    heightMm,
  );
  return heightMm;
}

function writeOnePage(doc, page, band, pageIndex, pageCount, layout) {
  if (pageIndex > 0) {
    doc.addPage();
  }
  if (layout.headerLines.length) {
    drawLines(
      doc,
      layout.headerLines,
      HEADER_FONT_SIZE,
      true,
      TEXT_DARK_RGB,
      layout.pageWidth / 2,
      layout.margin.top + HEADER_FONT_SIZE * LINE_HEIGHT_RATIO,
    );
  }
  // Bölünen tablonun başlığı yeni sayfanın üstünde tekrarlanır.
  const repeatHeight = page.repeat
    ? drawCanvasBand(doc, page.repeat, 0, page.repeat.height, layout, layout.contentTop)
    : 0;
  drawCanvasBand(
    doc,
    band,
    page.start - band.top,
    page.end - page.start,
    layout,
    layout.contentTop + repeatHeight,
  );
  if (layout.footerLines.length) {
    drawLines(
      doc,
      layout.footerLines,
      FOOTER_FONT_SIZE,
      true,
      TEXT_MUTED_RGB,
      layout.pageWidth / 2,
      layout.footerTop + FOOTER_FONT_SIZE * LINE_HEIGHT_RATIO,
    );
  }
  if (layout.pageLabel) {
    drawLines(
      doc,
      [layout.pageLabel(pageIndex + 1, pageCount)],
      PAGE_LABEL_FONT_SIZE,
      false,
      TEXT_MUTED_RGB,
      layout.pageWidth / 2,
      layout.pageHeight - layout.margin.bottom,
    );
  }
  if (layout.watermark) {
    drawPdfWatermark(doc, layout.watermark, layout.pageWidth, layout.pageHeight);
  }
}

// Bantlar sırayla render edilir ve sayfaları yazıldıktan sonra canvas'ı bırakılır;
// hepsi birden bellekte tutulursa uzun içerikte sekme çöker.
function writeDocument(doc, host, html2canvas, plan, layout, options) {
  const pages = plan.pages.filter((page) => page.end - page.start > 0);
  let written = 0;
  return plan.bands.reduce(
    (chain, band, bandIndex) =>
      chain.then(() =>
        renderRegion(host, html2canvas, band, options).then((canvas) => {
          band.canvas = canvas;
          pages.forEach((page) => {
            if (page.bandIndex === bandIndex) {
              writeOnePage(doc, page, band, written, pages.length, layout);
              written++;
            }
          });
          band.canvas = null;
        }),
      ),
    Promise.resolve(),
  );
}

// config: { html, hostClass, fileName, orientation, pageSize, margin, breakSelectors,
//           repeatHeaderSelector, header, footer, watermark, pageLabel, backgroundColor, renderWidth }
// renderWidth: içeriğin yerleşeceği CSS px genişliği; verilmezse sayfa genişliğine eşittir.
// repeatHeaderSelector: thead'i sayfa devrinde tekrarlanacak tablolar.
// html gövde parçasıdır, ana dokümanın stilleriyle render edilir.
// header/footer/watermark her sayfada tekrarlanır, metinleri localize gelmelidir.
export function downloadHtmlAsPdf(config) {
  const cfg = config || {};
  const header = cfg.header || "";
  const footer = cfg.footer || "";
  const watermark = cfg.watermark || "";
  const pageLabel = cfg.pageLabel || null;
  const needsText = Boolean(header || footer || watermark || pageLabel);
  const margin = Object.assign({}, DEFAULT_MARGIN, cfg.margin);
  const backgroundColor = cfg.backgroundColor || "#ffffff";
  const baseName = cfg.fileName || "export";
  const fileName = baseName.toLowerCase().endsWith(".pdf") ? baseName : `${baseName}.pdf`;
  const breakSelectors = cfg.breakSelectors && cfg.breakSelectors.length ? cfg.breakSelectors : ["tr", "table", "img"];

  return waitForPaint()
    .then(() => loadModules(needsText))
    .then((modules) => {
      const JsPDF = modules[0].jsPDF;
      const html2canvas = modules[1].default;
      const doc = new JsPDF({
        orientation: cfg.orientation || "portrait",
        unit: "mm",
        format: cfg.pageSize || "a4",
        compress: true,
      });

      if (needsText) {
        doc.addFileToVFS("Roboto-Regular.ttf", modules[2].default);
        doc.addFileToVFS("Roboto-Bold.ttf", modules[3].default);
        doc.addFont("Roboto-Regular.ttf", FONT_NAME, "normal");
        doc.addFont("Roboto-Bold.ttf", FONT_NAME, "bold");
        doc.setFont(FONT_NAME, "normal");
      }

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - margin.left - margin.right;
      const headerLines = buildLines(doc, header, HEADER_FONT_SIZE, contentWidth, true);
      const footerLines = buildLines(doc, footer, FOOTER_FONT_SIZE, contentWidth, true);
      const headerHeight = headerLines.length ? headerLines.length * HEADER_FONT_SIZE * LINE_HEIGHT_RATIO + HEADER_GAP : 0;
      const footerHeight = footerLines.length ? footerLines.length * FOOTER_FONT_SIZE * LINE_HEIGHT_RATIO + FOOTER_GAP : 0;
      const pageLabelHeight = pageLabel ? PAGE_LABEL_FONT_SIZE * LINE_HEIGHT_RATIO + FOOTER_GAP : 0;
      const contentTop = margin.top + headerHeight;
      const contentHeight = Math.max(
        MIN_CONTENT_HEIGHT,
        pageHeight - contentTop - margin.bottom - footerHeight - pageLabelHeight,
      );
      // renderWidth verilirse içerik o genişlikte yerleşir ve sayfaya sığacak şekilde küçültülür;
      // böylece dar sayfada sütunlar sıkışıp satır sarmaz, ekrandaki düzen korunur.
      const baseWidthPx = mmToPx(contentWidth);
      const renderWidthPx = Math.round(cfg.renderWidth > 0 ? cfg.renderWidth : baseWidthPx);
      const renderScale = renderWidthPx / baseWidthPx;
      const layout = {
        pageWidth: pageWidth,
        pageHeight: pageHeight,
        margin: margin,
        contentTop: contentTop,
        contentWidth: contentWidth,
        renderScale: renderScale,
        footerTop: pageHeight - margin.bottom - pageLabelHeight - footerHeight + FOOTER_GAP,
        headerLines: headerLines,
        footerLines: footerLines,
        pageLabel: pageLabel,
        watermark: watermark,
        backgroundColor: backgroundColor,
      };

      const host = createRenderHost(cfg.html, renderWidthPx, cfg.hostClass);
      const renderOptions = {
        pageHeightPx: mmToPx(contentHeight) * renderScale,
        breakSelectors: breakSelectors,
        repeatHeaderSelector: cfg.repeatHeaderSelector || "table",
        backgroundColor: backgroundColor,
      };
      return capturePlan(host, html2canvas, renderOptions)
        .then((plan) => writeDocument(doc, host, html2canvas, plan, layout, renderOptions))
        .then(() => doc.save(fileName))
        .finally(() => removeRenderHost(host));
    });
}
